import { faEye, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Loading, Pagination } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { GET_ITEM_LIMIT } from "constants/constants"
import useCommon from "hooks/useCommon"
import { useEffect, useMemo, useState } from "react"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import {
  CONTRACT_TYPE_LABELS,
  Contract as BackendContract,
  ContractType,
  useDeleteContractMutation,
  useListContractOwnersQuery,
  useListContractSubscriptionYearsQuery,
  useListContractTypesQuery,
  useListContractsQuery,
  useListCountriesQuery,
} from "services/aimsBackend"
import ContractFormModal from "./AddContractPage"

type Option = { value: string; label: string }

function toOptions(items: unknown[] | undefined): Option[] {
  if (!items?.length) return []
  return items.map((it: any) => {
    const value = String(it.value ?? it.code ?? it.id ?? it.label ?? it.name ?? it)
    const label = String(it.label ?? it.name ?? it.title ?? value)
    return { value, label }
  })
}

export default function ContractsPage() {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(GET_ITEM_LIMIT)
  const [deleteTarget, setDeleteTarget] = useState<BackendContract | null>(null)
  const [showFormModal, setShowFormModal] = useState(false)

  const [ownerFilter, setOwnerFilter] = useState<Option[]>([])
  const [typeFilter, setTypeFilter] = useState<Option[]>([])
  const [yearFilter, setYearFilter] = useState<Option[]>([])
  const [countryFilter, setCountryFilter] = useState<Option[]>([])

  const {
    data: list,
    isFetching,
    error,
  } = useListContractsQuery({
    page: pageNumber,
    page_size: pageSize,
    owner: ownerFilter.map(option => option.value),
    subscription_year: yearFilter.map(option => Number(option.value)),
    country: countryFilter.map(option => option.value),
    contract_type: typeFilter.map(option => option.value),
  })
  const { data: contractTypes = [] } = useListContractTypesQuery()
  const { data: countries = [] } = useListCountriesQuery()
  const { data: contractOwners = [] } = useListContractOwnersQuery()
  const { data: contractYears = [] } = useListContractSubscriptionYearsQuery()
  const [deleteContract] = useDeleteContractMutation()

  const contracts = list?.results ?? []
  const totalCount = list?.count ?? 0

  const typeOptions = useMemo(() => {
    const fromApi = toOptions(contractTypes)
    if (fromApi.length) return fromApi
    return Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => ({ value, label }))
  }, [contractTypes])
  const countryOptions = useMemo<Option[]>(
    () => countries.map(country => ({ value: country.iso3, label: country.name })),
    [countries],
  )
  const countryNameByIso3 = useMemo(() => new Map(countries.map(country => [country.iso3, country.name])), [countries])
  const yearOptions = useMemo<Option[]>(
    () => [...contractYears].sort((a, b) => b - a).map(year => ({ value: String(year), label: String(year) })),
    [contractYears],
  )
  const ownerOptions = useMemo<Option[]>(
    () => contractOwners.map(owner => ({ value: owner, label: owner })),
    [contractOwners],
  )

  useEffect(() => {
    setPageNumber(1)
  }, [ownerFilter, typeFilter, yearFilter, countryFilter, pageSize])

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteContract(deleteTarget.id).unwrap()
      showSnackBar("Contract deleted successfully", "success")
    } catch (err: any) {
      const detail = err?.data?.detail
      showSnackBar(typeof detail === "string" ? detail : "Failed to delete contract", "error")
    } finally {
      setDeleteTarget(null)
    }
  }

  const requestDelete = (contract: BackendContract) => {
    const assetCount = contract.assets?.length ?? 0
    if (assetCount > 0) {
      showSnackBar(
        `This contract cannot be deleted because ${assetCount} asset${
          assetCount === 1 ? " is" : "s are"
        } connected to it.`,
        "error",
      )
      return
    }
    setDeleteTarget(contract)
  }

  const columns: Array<ColumnShape<BackendContract>> = [
    { key: "name", dataKey: "name", title: "Name", width: 200, flexGrow: 1 },
    { key: "owner", dataKey: "owner", title: "Owner", width: 160, flexGrow: 1 },
    {
      key: "type",
      title: "Type",
      width: 160,
      flexGrow: 1,
      cellRenderer: ({ rowData }) =>
        rowData.contract_types.map(t => CONTRACT_TYPE_LABELS[t as ContractType] ?? t).join(", "),
    },
    {
      key: "subscriptionYear",
      dataKey: "subscription_year",
      title: "Subscription year",
      width: 140,
      flexGrow: 1,
    },
    {
      key: "countries",
      title: "Countries",
      width: 160,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => rowData.countries.map(c => countryNameByIso3.get(c) ?? c).join(", "),
    },
    {
      key: "action",
      width: 200,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <div className="d-flex">
          <Button
            action={() => navigate(`/aims/Contracts/details/${rowData.id}`)}
            label="Preview"
            icon={faEye}
            className="btn-full blue action-btn"
          />
          <Button
            action={() => requestDelete(rowData)}
            label={(rowData.assets?.length ?? 0) > 0 ? "Delete unavailable" : "Delete"}
            icon={faTrash}
            className="btn-full danger action-btn ml-2"
            style={(rowData.assets?.length ?? 0) > 0 ? { cursor: "not-allowed", opacity: 0.65 } : undefined}
            title={
              (rowData.assets?.length ?? 0) > 0
                ? "Cannot delete this contract because assets are connected to it"
                : "Delete contract"
            }
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <h2 className="m-0 mb-3 mt-2">Contracts</h2>
      <HeaderFilters>
        <Loading active={isFetching} />
        <div className="d-flex" style={{ width: "80%" }} />
        <Button action={() => setShowFormModal(true)} label="Add contract" className="btn-full green" icon={faPlus} />
      </HeaderFilters>

      <div className="mt-2 mb-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Subscription Year
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={yearOptions}
            isMulti
            value={yearFilter}
            onChange={val => setYearFilter(val as Option[])}
            placeholder="Select years"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Owner
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={ownerOptions}
            isMulti
            value={ownerFilter}
            onChange={val => setOwnerFilter(val as Option[])}
            placeholder="Select owners"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Contract Type
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={typeOptions}
            isMulti
            value={typeFilter}
            onChange={val => setTypeFilter(val as Option[])}
            placeholder="Select types"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Country
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={countryOptions}
            isMulti
            value={countryFilter}
            onChange={val => setCountryFilter(val as Option[])}
            placeholder="Select countries"
            isClearable
          />
        </div>
      </div>

      {error && (
        <div className="mb-2" style={{ color: "#da1e28", fontSize: 13 }}>
          Failed to load contracts. Check the backend connection.
        </div>
      )}

      <div style={{ height: "100%", minHeight: "60vh", marginTop: "10px" }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={contracts}
              width={width}
              height={height}
              rowKey="id"
              className="custom-table"
              rowHeight={38}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
            />
          )}
        </AutoResizer>
      </div>

      <Pagination
        totalItems={totalCount}
        pageSize={pageSize}
        pageSizes={[10, 20, 50]}
        page={pageNumber}
        onChange={(event: any): void => {
          const nextPageSize = (event as any).pageSize || pageSize
          setPageSize(nextPageSize)
          setPageNumber(nextPageSize === pageSize ? (event as any).page || 1 : 1)
        }}
      />

      <DeleteConfirmationModal
        open={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={handleDelete}
      >
        Are you sure you want to delete &quot;{deleteTarget?.name}&quot;?
      </DeleteConfirmationModal>

      <ContractFormModal open={showFormModal} onClose={() => setShowFormModal(false)} />
    </>
  )
}
